import React from 'react';

import styled from 'styled-components';
import Subtitle1 from '@twicapp/ui/components/Subtitle1';
import Body2 from '@twicapp/ui/components/Body2';

import { PRODUCT_PAGE_ELEMENTS } from '../../constants/testSelectors';

import Currency from './Currency';
import { getAmountToPoints } from 'utils/transaction';

interface PriceComparisonProps {
  new_layout?: boolean;
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PriceContainer = styled.div<PriceComparisonProps>`
  display: flex;
  gap: 8px;
  flex-direction: ${(props) => (props.new_layout ? 'row-reverse' : 'row')};
`;

const StrikeoutPrice = styled(Subtitle1)<PriceComparisonProps>`
  text-decoration: line-through;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
  font-weight: 400;
  font-size: ${(props) => (props.new_layout ? '16px' : '20px')};
`;

const ProductPrice = styled(Subtitle1)<PriceComparisonProps>`
  color: ${({
    theme: {
      colors: {
        text: { primary },
      },
    },
  }) => primary};
  font-weight: ${(props) => (props.new_layout ? '600' : '400')};
  font-size: ${(props) => (props.new_layout ? '16px' : '20px')};
`;

const SavingsBoxContainer = styled.div`
  display: flex;
`;

const SavingsBox = styled(Body2)<PriceComparisonProps>`
  display: block;
  background-color: ${({
    theme: {
      colors: {
        background: { quaternary },
      },
    },
  }) => quaternary};
  border-radius: ${(props) => (props.new_layout ? '16px' : '100px')};
  font-size: ${(props) => (props.new_layout ? '12px' : 'initial')};
  font-weight: ${(props) => (props.new_layout ? '600' : 'initial')};
  padding: 2px 12px;
`;

const PriceComparison = ({
  old_price,
  new_price,
  term,
  company,
  saving_bubble,
  new_layout,
}: {
  old_price: number | { value: number };
  new_price: number | { value: number };
  term: string;
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
      };
    };
  };
  saving_bubble: boolean;
  new_layout?: boolean;
}) => {
  const { display_as_amount } = company.features.stipend_config;

  // TODO: remove this crazy type-checking when we stop using points (twitch only for now)
  let price_difference = 0;
  if (typeof old_price === 'number' && typeof new_price === 'number') {
    price_difference = (old_price - new_price) / old_price;
  }
  if (typeof old_price === 'object' && typeof new_price === 'object') {
    const old_value = old_price.value as number;
    const new_value = new_price.value as number;
    price_difference = (old_value - new_value) / old_value;
  }
  const saving_by_percent = (price_difference * 100).toFixed();
  const savings_box_text = new_layout
    ? `${saving_by_percent}% OFF`
    : `Save ${saving_by_percent}%`;

  return (
    <Container>
      <PriceContainer
        data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE_CONTAINER}
        new_layout={new_layout}
      >
        {price_difference > 0 && (
          <StrikeoutPrice
            data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_STRIKEOUT_PRICE}
            new_layout={new_layout}
          >
            {display_as_amount ? (
              <Currency amount={old_price} />
            ) : (
              getAmountToPoints({
                company,
                total: old_price,
                format: true,
              })
            )}
          </StrikeoutPrice>
        )}
        <ProductPrice
          data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE}
          new_layout={new_layout}
        >
          {display_as_amount ? (
            <Currency amount={new_price} />
          ) : (
            getAmountToPoints({
              company,
              total: new_price,
              format: true,
            })
          )}
          {term !== 'one_time' && '/mo'}
        </ProductPrice>
      </PriceContainer>
      {price_difference > 0 && saving_bubble && (
        <SavingsBoxContainer>
          <SavingsBox new_layout={new_layout}>{savings_box_text}</SavingsBox>
        </SavingsBoxContainer>
      )}
    </Container>
  );
};

export default PriceComparison;
